import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { Home, Users, Briefcase, MessageCircle, MoreHorizontal } from "lucide-react";

type Tab = {
  id: string;
  label: string;
  icon: typeof Home;
  to: string;
  isActive: (pathname: string, panel: string | null) => boolean;
};

const TABS: Tab[] = [
  {
    id: "home",
    label: "Home",
    icon: Home,
    to: "/",
    isActive: (pathname, panel) => pathname === "/" && !panel,
  },
  {
    id: "students",
    label: "Students",
    icon: Users,
    to: "/?panel=families&tab=children",
    isActive: (pathname, panel) => panel === "families" || pathname.startsWith("/students"),
  },
  {
    id: "employees",
    label: "Employees",
    icon: Briefcase,
    to: "/?panel=employees&tab=directory",
    isActive: (pathname, panel) => panel === "employees" || pathname.startsWith("/employees"),
  },
  {
    id: "messages",
    label: "Messages",
    icon: MessageCircle,
    to: "/messaging",
    isActive: (pathname) => pathname.startsWith("/messaging"),
  },
];

type Props = {
  unreadCount?: number;
  onMorePress: () => void;
};

export function AdminBottomTabBar({ unreadCount = 0, onMorePress }: Props) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const panel = searchParams.get("panel");

  return (
    <nav
      data-tour="tab-bar"
      className="fixed left-0 right-0 z-50 mx-auto pointer-events-auto flex w-[90%] max-w-sm items-center justify-around rounded-[24px] border border-border/50 bg-surface/95 px-2 py-2 shadow-xl backdrop-blur-xl"
      style={{ bottom: "max(1rem, env(safe-area-inset-bottom, 0px))" }}
    >
      {TABS.map(({ id, label, icon: Icon, to, isActive: checkActive }) => {
        const active = checkActive(location.pathname, panel);
        return (
          <button
            key={id}
            data-tour={`mobile-tab-${id}`}
            onClick={() => navigate(to)}
            className={`relative flex flex-1 flex-col items-center gap-1 rounded-xl py-1.5 transition-all ${
              active ? "text-accent bg-accent/10" : "text-muted hover:bg-surface2"
            }`}
          >
            <div className="relative">
              <Icon size={22} strokeWidth={active ? 2.5 : 2} />
              {id === "messages" && unreadCount > 0 && (
                <span
                  className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-danger border border-surface shadow-sm"
                  aria-hidden="true"
                />
              )}
            </div>
            <span className="text-[10px] font-semibold tracking-wide">{label}</span>
            {id === "messages" && unreadCount > 0 && (
              <span className="sr-only">
                {unreadCount} unread message{unreadCount !== 1 ? "s" : ""}
              </span>
            )}
          </button>
        );
      })}

      {/* More tab — never "active" */}
      <button
        data-tour="mobile-tab-more"
        onClick={onMorePress}
        className="relative flex flex-1 flex-col items-center gap-1 rounded-xl py-1.5 transition-all text-muted hover:bg-surface2"
      >
        <MoreHorizontal size={22} strokeWidth={2} />
        <span className="text-[10px] font-semibold tracking-wide">More</span>
      </button>
    </nav>
  );
}
